import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { IAlerta } from 'modelosSeguridad/src';
import { BaseEntityLayerManager } from '../core/base/base-entity-layer-manager';

/**
 * AlertasSeguridadLayerManager
 *
 * Renderiza alertas de seguridad (botón de pánico) en el mapa general.
 * Color por estadoActual:
 *  - Sin Tratamiento → rojo
 *  - En Proceso → azul
 *  - Pendiente / Liberada → amarillo
 *  - Derivada Finalizada / Finalizada → verde (semi-transparente)
 *
 * Visual: círculo coloreado (estado) + icono triángulo encima.
 */
@Injectable({
  providedIn: 'root',
})
export class AlertasSeguridadLayerManager extends BaseEntityLayerManager<IAlerta> {
  readonly entityType = 'alertasSeguridad';
  readonly supportsClustering = true;
  readonly clusterColor = '#E53935';

  private mainLayerId = 'alertasSeguridad-layer';
  private circleLayerId = 'alertasSeguridad-circle-layer';
  private unclusteredLayerId = 'alertasSeguridad-unclustered-layer';
  private unclusteredCircleLayerId = 'alertasSeguridad-unclustered-circle-layer';

  private readonly estadoColorMap: Record<string, string> = {
    'Sin Tratamiento': '#E53935', // rojo
    'En Proceso': '#1E88E5', // azul
    Pendiente: '#F9A825', // amarillo
    Liberada: '#F9A825',
    Finalizada: '#43A047', // verde
    'Derivada Finalizada': '#43A047',
  };

  private readonly defaultColor = '#9E9E9E'; // gris

  override entityToFeature(alerta: IAlerta): GeoJSON.Feature<GeoJSON.Point> {
    this.storeEntity(alerta);

    let coordinates: [number, number];

    if (
      alerta.geojson?.coordinates &&
      Array.isArray(alerta.geojson.coordinates) &&
      alerta.geojson.coordinates.length === 2
    ) {
      coordinates = alerta.geojson.coordinates as [number, number];
    } else if (
      alerta.ubicacion &&
      typeof alerta.ubicacion.lng === 'number' &&
      typeof alerta.ubicacion.lat === 'number'
    ) {
      coordinates = [alerta.ubicacion.lng, alerta.ubicacion.lat];
    } else {
      console.warn(`Alerta seguridad ${alerta._id} sin coordenadas válidas`);
      coordinates = [-58.443832, -34.587386];
    }

    const estado = alerta.estadoActual || 'Sin Tratamiento';
    const color = this.estadoColorMap[estado] ?? this.defaultColor;
    const finalizada =
      estado === 'Finalizada' || estado === 'Derivada Finalizada';

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates,
      },
      properties: {
        id: alerta._id,
        estadoActual: estado,
        color,
        finalizada,
        fechaCreacion: alerta.fechaCreacion,
        idCliente: alerta.idCliente,
        prioridad: alerta.prioridad,
      },
    };
  }

  override getIconExpression(): string {
    return 'alerta-seguridad-icon';
  }

  private getCircleColorExpression(): mapboxgl.Expression {
    return [
      'match',
      ['get', 'estadoActual'],
      'Sin Tratamiento',
      this.estadoColorMap['Sin Tratamiento'],
      'En Proceso',
      this.estadoColorMap['En Proceso'],
      'Pendiente',
      this.estadoColorMap['Pendiente'],
      'Liberada',
      this.estadoColorMap['Liberada'],
      'Finalizada',
      this.estadoColorMap['Finalizada'],
      'Derivada Finalizada',
      this.estadoColorMap['Derivada Finalizada'],
      this.defaultColor,
    ];
  }

  private getOpacityExpression(): mapboxgl.Expression {
    return ['case', ['get', 'finalizada'], 0.55, 1];
  }

  protected override createLayers(): void {
    if (!this.map) return;

    if (this.supportsClustering) {
      this.createStandardClusterLayers();

      // Círculo coloreado por estado (debajo del icono)
      this.addLayer({
        id: this.unclusteredCircleLayerId,
        type: 'circle',
        source: this.clusteredSourceId,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-radius': 12,
          'circle-color': this.getCircleColorExpression(),
          'circle-opacity': 1,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
        layout: { visibility: 'none' },
      });

      this.addLayer({
        id: this.unclusteredLayerId,
        type: 'symbol',
        source: this.clusteredSourceId,
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': this.getIconExpression(),
          'icon-size': 0.02,
          'icon-allow-overlap': true,
          'icon-padding': 0,
          visibility: 'none',
        },
        paint: {
          'icon-opacity': this.getOpacityExpression() as any,
        },
      });
    }

    // Círculo coloreado debajo del icono principal
    this.addLayer({
      id: this.circleLayerId,
      type: 'circle',
      source: this.sourceId,
      paint: {
        'circle-radius': 12,
        'circle-color': this.getCircleColorExpression(),
        'circle-opacity': this.getOpacityExpression() as any,
        'circle-stroke-width': 1.5,
        'circle-stroke-color': '#ffffff',
      },
    });

    this.addLayer({
      id: this.mainLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'icon-image': this.getIconExpression(),
        'icon-size': 0.02,
        'icon-allow-overlap': true,
        'icon-padding': 0,
      },
      paint: {
        'icon-opacity': this.getOpacityExpression() as any,
      },
    });
  }

  protected override updateClusteringVisibility(): void {
    if (!this.map || !this.supportsClustering) return;

    if (this.clusteringEnabled) {
      this.setLayerVisibility(
        `${this.entityType}-cluster-layer`,
        this.isVisible,
      );
      this.setLayerVisibility(
        `${this.entityType}-cluster-count-layer`,
        this.isVisible,
      );
      this.setLayerVisibility(this.unclusteredCircleLayerId, this.isVisible);
      this.setLayerVisibility(this.unclusteredLayerId, this.isVisible);
      this.setLayerVisibility(this.circleLayerId, false);
      this.setLayerVisibility(this.mainLayerId, false);
    } else {
      this.setLayerVisibility(`${this.entityType}-cluster-layer`, false);
      this.setLayerVisibility(`${this.entityType}-cluster-count-layer`, false);
      this.setLayerVisibility(this.unclusteredCircleLayerId, false);
      this.setLayerVisibility(this.unclusteredLayerId, false);
      this.setLayerVisibility(this.circleLayerId, this.isVisible);
      this.setLayerVisibility(this.mainLayerId, this.isVisible);
    }
  }

  private setLayerVisibility(layerId: string, visible: boolean): void {
    if (!this.map) return;
    if (this.map.getLayer(layerId)) {
      this.map.setLayoutProperty(
        layerId,
        'visibility',
        visible ? 'visible' : 'none',
      );
    }
  }

  override setVisibility(visible: boolean): void {
    if (!this.map) return;

    this.isVisible = visible;

    if (this.clusteringEnabled && this.supportsClustering) {
      this.setLayerVisibility(`${this.entityType}-cluster-layer`, visible);
      this.setLayerVisibility(
        `${this.entityType}-cluster-count-layer`,
        visible,
      );
      this.setLayerVisibility(this.unclusteredCircleLayerId, visible);
      this.setLayerVisibility(this.unclusteredLayerId, visible);
    } else {
      this.setLayerVisibility(this.circleLayerId, visible);
      this.setLayerVisibility(this.mainLayerId, visible);
    }
  }
}
